import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAPI } from '../../API';
import { useAppDispatch, useAppState } from '../../Store';
import { useTranslation } from '../../Translation';
import SidebarNav from '../../Components/SidebarNav/SidebarNav';
import SettingSidebarNav from '../../Components/SidebarNav/SettingSidebarNav';
import useAuthenticated from '../Dashboard/useAuthenticated';
import Badge from '../../Components/Badge';
import { TextInput } from '../../Components/Inputs/TextInput';
import { Button } from "../../Components/Buttons";
import { boldWordInText } from '../../Utils';
import { ActionTypes } from '../../Store/Stores/User';
import { useRefresh } from '../../Hooks/useRefresh';
import Modal from '../../Components/Modal';

export default function Apps() {


    const { translate, language, setLanguage } = useTranslation();
    const api = useAPI();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const appState = useAppState();
    const { user } = appState;
    const [apps, setApps] = useState([]);
    const [userApps, setUserApps] = useState([]);
    const [open, setOpen] = useState(false);
    const [app, setApp] = useState(null);

    const {refreshId, refresh} = useRefresh();

    const { isAuthenticated, pathAfterLogin } = useAuthenticated();

    useEffect(() => {

        api.listApps(1, 99999).then((response) => {
            setApps(response.data.apps);
        });

        api.listUserApps(1, 99999).then((response) => {
            setUserApps(response.data.user_apps);
        });
    }, [refreshId]);

    const appsNotConfigured = apps.filter((app) => !userApps.some((userApp) => userApp.app_id === app.id));
    const appsConfigured = apps.filter((app) => userApps.some((userApp) => userApp.app_id === app.id));


    const selectedUserApp = userApps.find((userApp) => userApp.app_id === app?.id);
    const isSelectedAppConfigured = selectedUserApp ? true : false;

    return (
        <>
        <Modal
                open={open}
                onClose={() => setOpen(false)}
                title={isSelectedAppConfigured ? translate('edit') + ' ' + app?.name : translate('create') + ' ' + app?.name}
                description={app?.description}
                children={<AppModal refresh={refresh} setOpen={setOpen} app={app} userApp={selectedUserApp} open={open} />}
            />
        <div className="flex min-h-full w-full">
            <SidebarNav currentPage="settings" contentPadding={false}>
                <div className='flex-1 flex-row h-full w-full'>
                    <SettingSidebarNav currentPage="apps" />
                    <div className='flex-1 flex flex-col ml-24 md:ml-72'>
                        <div className='flex flex-col p-2 md:p-4 lg:p-6 w-full'>
                            <h3 className='text-2xl font-bold'>{translate('setting.apps.title')}</h3>
                            <p className='text-md text-gray-500 max-w-4xl'>{translate('setting.apps.description')}</p>

                            <div className='flex flex-col mt-8'>
                                <h3 className='text-lg font-bold'>{translate('setting.apps.configured_apps')}</h3>
                                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 p-8 md:px-0 gap-4 mt-4 border-t border-gray-200 pt-4 max-w-4xl'>
                                    {appsConfigured.map((app) => (
                                        <div onClick={() => {setApp(app); setOpen(true)}} key={app.id} className='flex flex-col items-center border aspect-square items-center justify-center border-gray-200 rounded-md p-4 bg-white cursor-pointer hover:bg-gray-100'>
                                            <img src={app.icon} alt={app.name} className='w-64 h-32 object-contain' />
                                            <p className='text-4xl font-bold text-black'>{app.name}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className='flex flex-col mt-8'>
                                <h3 className='text-lg font-bold'>{translate('setting.apps.not_configured_apps')}</h3>
                                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 p-8 md:px-0 gap-4 mt-4 border-t border-gray-200 pt-4 max-w-4xl'>
                                    {appsNotConfigured.map((app) => (
                                        <div onClick={() => {setApp(app); setOpen(true)}} key={app.id} className='flex flex-col items-center border aspect-square items-center justify-center border-gray-200 rounded-md p-4 bg-white cursor-pointer hover:bg-gray-100'>
                                            <img src={app.icon} alt={app.name} className='w-64 h-32 object-contain' />
                                            <p className='text-4xl font-bold text-black'>{app.name}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                    </div>
                </div>
                
            </SidebarNav>
        </div>
        </>
        

    );

    
}


export const AppModal = ({ refresh, setOpen, app, userApp, open }) => {

    const { translate } = useTranslation();
    const api = useAPI();
    const [fields, setFields] = useState(app.data);
    const handleSave = async () => {
        try {

            const data = fields.reduce((acc, field) => {
                acc[field.field] = field.value;
                return acc;
            }, {});

            const newUserApp = {
                app_id: app.id,
                data: data
            };


            if (userApp) {
                await api.updateUserApp(userApp.id, newUserApp);
                refresh();
                setOpen(false);
            } else {
                await api.createUserApp(newUserApp);
                refresh();
                setOpen(false);
            }
           
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        if (open && userApp) {
            const newFields = app.data.map((field) => {
                return {
                    ...field,
                    value: userApp.data[field.field]
                }
            });
            setFields(newFields);
        }
    }, [open, userApp, app]);

    const setFieldValue = (id, value) => {
        setFields(fields.map((field) => field.field === id ? { ...field, value } : field));
    }

    const handleDelete = async () => {
        await api.deleteUserApp(userApp.id);
        refresh();
        setOpen(false);
    }

    return (
        <div className="mt-4 overflow-visible">
            {fields.map((field) => (
                <div key={field.id}>
                    <TextInput 
                        id={field.field} 
                        name={field.field} 
                        label={field.name} 
                        placeholder={field.placeholder} 
                        value={field.value} 
                        type={field.type === 'string' ? 'text' : 'number'}
                        onChange={(e) => setFieldValue(field.field, e.target.value)} 
                        required={field.required} />
                </div>
            ))}
            <div className="flex justify-end mt-4">
                {userApp && <Button className='mr-4' onClick={handleDelete}>{translate('delete')}</Button>}
                <Button className={'bg-green-500'} onClick={handleSave}>{translate('save')}</Button>
            </div>
        </div>
    )
}